import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Bell, MessageSquare, Award } from "lucide-react";
import { listForumNotifications, markForumNotificationsRead } from "@/lib/forum.functions";

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "nu";
  if (m < 60) return `${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h`;
  return `${Math.round(h / 24)} d`;
}

export function ForumNotificationsBell() {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const qc = useQueryClient();
  const load = useServerFn(listForumNotifications);
  const markRead = useServerFn(markForumNotificationsRead);

  const { data } = useQuery({
    queryKey: ["forum-notifications"],
    queryFn: () => load(),
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const unread = data?.unread ?? 0;
  const items = data?.items ?? [];

  async function openPanel() {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      await markRead({ data: {} }).catch(() => null);
      qc.invalidateQueries({ queryKey: ["forum-notifications"] });
    }
  }

  return (
    <div ref={boxRef} className="relative">
      <button
        onClick={openPanel}
        aria-label={unread > 0 ? `${unread} olästa notiser` : "Notiser"}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-foreground/80 transition hover:bg-secondary hover:text-foreground"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid min-w-[18px] place-items-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-4 text-white">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold">Notiser</div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              Inga notiser än. Svara i{" "}
              <Link to="/forum" className="underline" onClick={() => setOpen(false)}>
                forumet
              </Link>{" "}
              för att komma igång.
            </p>
          ) : (
            <ul className="max-h-[60vh] divide-y divide-border overflow-y-auto">
              {items.map((n) => (
                <li key={n.id}>
                  <Link
                    to="/forum/t/$slug"
                    params={{ slug: n.post_slug ?? "" }}
                    hash={n.comment_id ? `comment-${n.comment_id}` : undefined}
                    onClick={() => setOpen(false)}
                    className={`flex gap-3 px-4 py-3 transition hover:bg-secondary ${n.read_at ? "" : "bg-primary/5"}`}
                  >
                    <span className="mt-0.5 text-primary">
                      {n.type === "best_answer" ? <Award size={16} /> : <MessageSquare size={16} />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium leading-snug">
                        {n.type === "best_answer"
                          ? "Ditt svar valdes som bästa svar"
                          : n.type === "subscribed_activity"
                          ? `${n.actor_name ?? "Någon"} svarade i en tråd du bevakar`
                          : `${n.actor_name ?? "Någon"} svarade ${n.type === "reply_to_comment" ? "på din kommentar" : "i din tråd"}`}
                      </span>
                      <span className="block truncate text-xs text-muted-foreground">{n.post_title}</span>
                      {n.excerpt && (
                        <span className="mt-1 block line-clamp-2 text-xs text-foreground/70">{n.excerpt}</span>
                      )}
                      <span className="mt-1 block text-[11px] text-muted-foreground">{timeAgo(n.created_at)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
